/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  HalfContetntLayout,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import CustomGrid from 'components/grids/CustomGrid';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { MultiComboBox } from 'components/selects/ComboBox';
import { findMdIvesMdlLnEqpList } from 'apis/md/MdProject';
import { MdIvesMdlLnEqp, MdIvesMdlLnEqpCondition } from 'models/md/MdIves';
import { findMdBldoList } from 'apis/md/MdBldo';

type Props = {
  open: boolean;
  close: () => void;
  callbackFn: (items: MdIvesMdlLnEqp[]) => void;
  ivesMdlId?: string;
  elmPrjId?: string;
  prdnLnTpCds?: string[];
  selectedItems: MdIvesMdlLnEqp[];
};

/**
 * 라인 추가 팝업
 */
const LineInqPopup = ({ open, close, ivesMdlId, elmPrjId, prdnLnTpCds = [], ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const gridRef = useRef<any>();
  const selectedGridRef = useRef<any>();
  const [condition, setCondition] = useState<MdIvesMdlLnEqpCondition>({
    prdnLnTpCds,
    prdnLnTpCd: prdnLnTpCds.join(),
  });
  const [rowData, setRowData] = useState<MdIvesMdlLnEqp[]>([]);
  const [selectedItems, setSelectedItems] = useState<MdIvesMdlLnEqp[]>(props.selectedItems);
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [prdnLnTpCode, setPrdnLnTpCode] = useState<Code[]>([]);
  const [bldoCode, setBldoCode] = useState<Code[]>([]);
  const [eqclCode, setEqclCode] = useState<Code[]>([]);

  const layoutDefinition = [
    {
      header: String(t('com.label.라인유형', '라인유형')),
      binding: 'prdnLnTpCdNm',
      align: 'left',
      width: '*',
    },
    {
      binding: 'bildPlntCd',
      header: String(t('com.label.플랜트', '플랜트')),
      width: '*',
    },
    {
      binding: 'bldoNm',
      header: String(t('com.label.동', '동')),
      align: 'left',
      width: '*',
    },
    {
      binding: 'prdnLnNm',
      header: String(t('com.label.호기명', '호기명')),
      align: 'left',
      width: '*',
    },
    {
      binding: 'eqclNm',
      header: String(t('com.label.설비명', '설비명')),
      align: 'left',
      width: '*',
    },
    {
      binding: 'eqpCnt',
      header: String(t('com.label.설비대수(대)', '설비대수(대)')),
      align: 'left',
      width: '*',
    },
  ];

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    const MD_PRDN_LN_TP_CD = await getCommonCodeNames('MD_PRDN_LN_TP_CD');
    const MD_EQCL_ID = await getCommonCodeNames('MD_EQCL_ID');
    const BLDO_LIST = await findMdBldoList({});
    setBildPlntCode(bildPlntCode.map((o) => ({ ...o, cmnCdNm: o.cmnCd })));
    setPrdnLnTpCode(MD_PRDN_LN_TP_CD);
    setBldoCode(BLDO_LIST.map((o) => ({ cmnCd: o.bldoId, cmnCdNm: o.bldoNm })));
    setEqclCode(MD_EQCL_ID);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
    if (name === 'bildPlntCds') {
      setCondition((prev) => ({ ...prev, bildPlntCd: value.join() }));
    } else if (name === 'prdnLnTpCds') {
      setCondition((prev) => ({ ...prev, prdnLnTpCd: value.join() }));
    } else if (name === 'bldoIds') {
      setCondition((prev) => ({ ...prev, bldoId: value.join() }));
    } else if (name == 'eqclIds') {
      setCondition((prev) => ({ ...prev, eqclId: value.join() }));
    }
  };

  const handleAddRow = () => {
    setSelectedItems((prev) =>
      _.uniqBy(
        [...prev, ...gridRef.current.selectedItems],
        (o) => `${o.prdnLnTpCd}_${o.bildPlntCd}_${o.bldoId}_${o.prdnLnId}`
      )
    );
  };

  const handleDelRow = () => {
    const result = selectedItems.filter((o) => !selectedGridRef.current.selectedItems.includes(o));
    setSelectedItems(result);
  };

  const handleConfirm = () => {
    props.callbackFn(selectedItems);
    close();
  };

  const handleSearch = () => {
    openLoading(true);
    findMdIvesMdlLnEqpList(
      elmPrjId,
      condition.prdnLnTpCd,
      condition.bildPlntCd,
      condition.bldoId,
      condition.eqclId
    )
      .then((res) => {
        setRowData(res);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const onInitialized = (grid, gbn: string) => {
    if (gbn === 'A') {
      gridRef.current = grid;
    } else {
      selectedGridRef.current = grid;
    }
  };

  useEffect(() => {
    initCodes();
    handleSearch();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('md.label.생산라인 조회', '생산라인 조회'))}
      size="xl"
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('com.label.라인유형', '라인유형')}</label>
                <MultiComboBox
                  id="prdnLnTpCode"
                  options={prdnLnTpCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition.prdnLnTpCds}
                  onChange={(value) => handleOnChange('prdnLnTpCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.플랜트', '플랜트')}</label>
                <MultiComboBox
                  id="bildPlntCds"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition.bildPlntCds}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.동', '동')}</label>
                <MultiComboBox
                  id="bldoIds"
                  options={bldoCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition.bldoIds}
                  onChange={(value) => handleOnChange('bldoIds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.설비군', '설비군')}</label>
                <MultiComboBox
                  id="eqclIds"
                  options={eqclCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition.eqclIds}
                  onChange={(value) => handleOnChange('eqclIds', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={{
              userId: '',
              empNm: '',
              deptNm: '',
              useYn: 'Y',
            }}
            setReset={setCondition}
            onSearch={handleSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <HalfContetntLayout style={{ marginTop: '24px' }}>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.생산라인 목록', '생산라인 목록')}</h3>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="lineGrid"
            layoutDefinition={layoutDefinition}
            rowData={rowData}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={(grid) => onInitialized(grid, 'A')}
          />
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup></SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.label.삭제', '삭제')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="selectedLineGrid"
            layoutDefinition={layoutDefinition}
            rowData={selectedItems}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={(grid) => onInitialized(grid, 'B')}
          />
        </div>
      </HalfContetntLayout>
    </CustomDialog>
  );
};

export default LineInqPopup;
